import _ from "lodash";
import api from "../api/api";
import IDealStage from "../interfaces/IDealStage";
import IActivity from "../interfaces/IActivity";
import ICreateDealNoteResponse from "../interfaces/ICreateDealNoteResponse";
import IDeal, { DealShape, DealStatus, IDealState } from "../interfaces/IDeal";
import { IDealField } from "../interfaces/IDealField";
import IDealLabelOption from "../interfaces/IDealLabelOption";
import IDealNote from "../interfaces/IDealNote";
import IDealMailMessage from "../interfaces/IDealMailMessage";
import IPipedriveFileInfo from "../interfaces/IPipedriveFileInfo";
import IPipedriveOrganization from "../interfaces/IPipedriveOrganization";
import { IPipedriveOrganizationLogo } from "../interfaces/IPipedriveOrganizationLogo";
import IPipedrivePerson from "../interfaces/IPipedrivePerson";
import IPipedriveUser from "../interfaces/IPipedriveUser";
import { formatDateForInput, parseDate, parseDateTime } from "../utils/utils";
import DealFieldsProvider from "./dealFieldsProvider";

export default class PipedriveApiService {
    constructor(
        private dealFieldsProvider: DealFieldsProvider,
    ) { }

    public async getDealFields(): Promise<IDealField[]> {
        const response = await api.get<IDealField[]>("/pipedrive/dealFields");
        return response.data;
    }

    public async createDealLabel(label: string): Promise<IDealLabelOption> {
        const response = await api.post<IDealLabelOption>("/pipedrive/dealLabels", { label });
        return response.data;
    }

    public async getUsers(): Promise<IPipedriveUser[]> {
        const response = await api.get<IPipedriveUser[]>("/pipedrive/users");
        return response.data;
    }

    public async getCompanyDomain(): Promise<string> {
        const response = await api.get<string>("/pipedrive/companyDomain");
        return response.data;
    }

    public async getOrganizations(): Promise<IPipedriveOrganization[]> {
        const response = await api.get<IPipedriveOrganization[]>("/pipedrive/organizations");

        return response.data.map(o => ({
            ...o,
            createdDate: new Date(o.createdDate),
        }));
    }

    public async getAllOrganizationLogos(): Promise<IPipedriveOrganizationLogo[]> {
        const response = await api.get<IPipedriveOrganizationLogo[]>("/pipedrive/organizationLogos");
        return response.data;
    }

    public async searchOrganizations(query: string): Promise<IPipedriveOrganization[]> {
        const response = await api.get<IPipedriveOrganization[]>(`/pipedrive/searchOrganizations?query=${encodeURIComponent(query)}`);

        return response.data.map(o => ({
            ...o,
            createdDate: new Date(o.createdDate),
        }));
    }

    public async getPersons(): Promise<IPipedrivePerson[]> {
        const response = await api.get<IPipedrivePerson[]>("/pipedrive/persons");

        return response.data.map(o => ({
            ...o,
            createdDate: new Date(o.createdDate),
        }));
    }

    public async searchPersons(query: string): Promise<IPipedrivePerson[]> {
        const response = await api.get<IPipedrivePerson[]>(`/pipedrive/searchPersons?query=${encodeURIComponent(query)}`);

        return response.data.map(o => ({
            ...o,
            createdDate: new Date(o.createdDate),
        }));
    }

    public async createOrganization(name: string): Promise<IPipedriveOrganization> {
        const response = await api.post<IPipedriveOrganization>("/pipedrive/organizations", { name });

        return {
            ...response.data,
            createdDate: new Date(response.data.createdDate),
        };
    }

    public async createPerson(name: string): Promise<IPipedrivePerson> {
        const response = await api.post<IPipedrivePerson>("/pipedrive/persons", { name });

        return {
            ...response.data,
            createdDate: new Date(response.data.createdDate),
        };
    }

    public async updateOrganization(organization: IPipedriveOrganization): Promise<IPipedriveOrganization> {
        const response = await api.put<IPipedriveOrganization>("/pipedrive/organizations", {
            id: organization.id,
            name: organization.name,
        });

        return {
            ...response.data,
            createdDate: new Date(response.data.createdDate),
        };
    }

    public async getActivities(): Promise<IActivity[]> {
        const response = await api.get<IActivity[]>("/pipedrive/activities");

        return response.data.map(a => ({
            ...a,
            dueDate: parseDate(a.dueDate)!,
            createdDate: new Date(a.createdDate),
            modifiedDate: new Date(a.modifiedDate),
        }));
    };

    public async createActivity(dueDate: Date, subject: string, notes: string, userId: number, organizationId: number | null): Promise<IActivity> {
        const response = await api.post<IActivity>("/pipedrive/activities", {
            userId,
            organizationId,
            subject,
            notes,
            dueDate: formatDateForInput(dueDate),
        });

        return {
            ...response.data,
            dueDate: parseDate(response.data.dueDate)!,
            createdDate: new Date(response.data.createdDate),
            modifiedDate: new Date(response.data.modifiedDate),
        };
    };

    public async updateActivity(id: number, dueDate: Date, subject: string, notes: string, userId: number, organizationId: number | null): Promise<IActivity> {
        const response = await api.put<IActivity>("/pipedrive/activities", {
            id,
            userId,
            organizationId,
            subject,
            notes,
            dueDate: formatDateForInput(dueDate),
        });

        return {
            ...response.data,
            dueDate: parseDate(response.data.dueDate)!,
            createdDate: new Date(response.data.createdDate),
            modifiedDate: new Date(response.data.modifiedDate),
        };
    };

    public async markActivityAsDone(id: number) {
        await api.put(`/pipedrive/activities/${id}/done`);
    };

    public async deleteActivity(id: number) {
        await api.delete(`/pipedrive/activities/${id}`);
    };

    public async getDealNotes(dealId: number): Promise<IDealNote[]> {
        const response = await api.get<IDealNote[]>(`/pipedrive/dealNotes?dealId=${dealId}`);

        return response.data.map(n => ({
            ...n,
            createdAt: new Date(n.createdAt),
        }));
    }

    public async getDealMailMessages(dealId: number): Promise<IDealMailMessage[]> {
        const response = await api.get<IDealMailMessage[]>(`/pipedrive/dealMailMessages?dealId=${dealId}`);

        return response.data.map(m => ({
            ...m,
            messageTime: new Date(m.messageTime),
        }));
    }

    public async getDealMailMessageBody(messageId: number): Promise<string> {
        const response = await api.get<string>(`/pipedrive/dealMailMessageBody?id=${messageId}`);
        return response.data;
    }

    public async getDealFiles(dealId: number): Promise<IPipedriveFileInfo[]> {
        const response = await api.get<IPipedriveFileInfo[]>(`/pipedrive/dealFiles?dealId=${dealId}`);

        return response.data.map(f => ({
            ...f,
            updatedAt: new Date(f.updatedAt),
        }));
    }

    public async createDealFile(blob: any, name: string, dealId: number): Promise<IPipedriveFileInfo> {
        const formData = new FormData();
        formData.append("file", blob, name);
        formData.append("deal_id", dealId.toString());

        const response = await api.post<IPipedriveFileInfo>("pipedrive/dealFiles", formData);

        return {
            ...response.data,
            updatedAt: new Date(response.data.updatedAt),
        };
    }

    public async deleteDealFile(fileId: number): Promise<void> {
        await api.delete(`/pipedrive/dealFiles?id=${fileId}`);
    }

    public async addDealNote(note: IDealNote): Promise<ICreateDealNoteResponse> {
        const response = await api.post<IDealNote>("/pipedrive/dealNotes", {
            userId: note.userId,
            dealId: note.dealId,
            content: note.content,
        });

        return {
            id: response.data.id,
            content: response.data.content,
            createdAt: new Date(response.data.createdAt),
        };
    }

    public async getDealStages(): Promise<IDealStage[]> {
        const response = await api.get<IDealStage[]>("/pipedrive/dealStages");
        return response.data;
    }

    public async getDealsByStages(stageIds: number[]): Promise<IDeal[]> {
        const responses = await Promise.all(
            stageIds.map(stageId => api.get(`/pipedrive/deals?stageId=${stageId}`))
        );

        return this.mapResponseToDeals(_.flatMap(responses, r => r.data));
    }

    public async updateDeal(deal: IDeal): Promise<IDeal> {
        const dealToUpload = await this.mapDealToUploadModel(deal);
        const response = await api.put("/pipedrive/deals", dealToUpload);

        const updatedDeal = this.mapResponseToDeals(response.data)[0];
        this.dealUpdated(updatedDeal);

        return updatedDeal;
    }

    public async createDeal(
        dealState: IDealState, contactPersonId: number | null, contactCompanyId: number | null, brokerId: number | null, propertyOwnerId: number | null,
        lat: number, lon: number, dealShapes: DealShape[]
    ): Promise<IDeal> {
        const dealToUpload = await this.mapDealToUploadModel({
            ...dealState,
            contactPersonId: contactPersonId,
            contactCompanyId: contactCompanyId,
            brokerId: brokerId,
            propertyOwnerId: propertyOwnerId,
            lat: lat,
            lon: lon,
            dealShapes: dealShapes,
        } as unknown as IDeal);

        const response = await api.post("/pipedrive/deals", dealToUpload);

        const createdDeal = this.mapResponseToDeals(response.data)[0];
        this.dealCreated(createdDeal);

        return createdDeal;
    }

    public async deleteDeal(id: number) {
        await api.delete(`/pipedrive/deals?id=${id}`);
    }

    public async getOrCreatePipedriveOrganization(organizationName: string): Promise<IPipedriveOrganization> {
        const organizations = await this.searchOrganizations(organizationName);
        return organizations.find(o => o.name === organizationName) || await this.createOrganization(organizationName);
    }

    public async getOrCreatePipedrivePerson(personName: string): Promise<IPipedrivePerson> {
        const persons = await this.searchPersons(personName);
        return persons.find(p => p.name === personName) || await this.createPerson(personName);
    }

    private async mapDealToUploadModel(deal: IDeal): Promise<any> {
        const fieldKeys = this.dealFieldsProvider.getFieldKeys();
        const dealToUpload: any = {
            title: deal.name,
            status: deal.status,
            value: deal.value,
            label: deal.labelId,
            [fieldKeys.userId]: deal.assignedToUserId,
            [fieldKeys.dealShapes]: JSON.stringify(deal.dealShapes),
            [fieldKeys.address]: deal.address,
            [fieldKeys.city]: deal.city,
            [fieldKeys.county]: deal.county,
            [fieldKeys.state]: deal.state,
            [fieldKeys.lat]: deal.lat,
            [fieldKeys.lon]: deal.lon,
            [fieldKeys.dealStage]: deal.dealStageId,
            [fieldKeys.OID]: deal.OID,
            [fieldKeys.tenant]: deal.tenant,
            [fieldKeys.askingPrice]: deal.askingPrice,
            [fieldKeys.zoning]: deal.zoning,
            [fieldKeys.buildingSize]: deal.buildingSize,
            [fieldKeys.landSizeAcres]: deal.landSizeAcres,
            [fieldKeys.usableLand]: deal.usableLand,
            [fieldKeys.dropboxLink]: deal.dropboxLink,
            [fieldKeys.parcelId]: deal.parcelId,
            [fieldKeys.nonFund]: deal.nonFund,
            [fieldKeys.dealProfile]: deal.dealProfile,
            [fieldKeys.sourcingMethod]: deal.sourcingMethod,
            [fieldKeys.contactCompany]: deal.contactCompanyId,
            [fieldKeys.personId]: deal.contactPersonId,
            [fieldKeys.broker]: deal.brokerId,
            [fieldKeys.propertyOwner]: deal.propertyOwnerId,
            [fieldKeys.leaseCompId]: JSON.stringify(deal.leaseCompIds),
            [fieldKeys.saleCompId]: JSON.stringify(deal.saleCompIds),
        };

        if (deal.id)
            dealToUpload.id = deal.id;

        if (deal.status === DealStatus.Lost)
            dealToUpload.lost_reason = deal.lostReason;

        if (fieldKeys.regridId)
            dealToUpload[fieldKeys.regridId] = deal.regridId;

        return dealToUpload;
    }

    private mapResponseToDeals(dealsData: any[]): IDeal[] {
        if (!Array.isArray(dealsData))
            dealsData = [dealsData];

        const fieldKeys = this.dealFieldsProvider.getFieldKeys();
        const jsonShapeKey = fieldKeys.dealShapes;
        const nonFundOptions = this.dealFieldsProvider.getNonFundOptions();
        const soldOptions = this.dealFieldsProvider.getSoldOptions();
        const profileOptions = this.dealFieldsProvider.getDealProfileOptions();
        const sourcingMethodOptions = this.dealFieldsProvider.getDealSourcingMethodOptions();

        return dealsData.map(dealData => {
            const leaseCompIdsString = dealData[fieldKeys.leaseCompId];
            const saleCompIdsString = dealData[fieldKeys.saleCompId];

            const deal: IDeal = {
                id: dealData.id,
                name: dealData[fieldKeys.name],
                assignedToUserId: dealData["user_id"].id,
                dealShapes: dealData[jsonShapeKey] && this.IsJsonString(dealData[jsonShapeKey]) ? JSON.parse(dealData[jsonShapeKey]) : [],
                state: dealData[fieldKeys.state],
                county: dealData[fieldKeys.county],
                city: dealData[fieldKeys.city],
                contactPersonId: dealData[fieldKeys.personId]?.value,
                address: dealData[fieldKeys.address],
                lat: dealData[fieldKeys.lat],
                lon: dealData[fieldKeys.lon],
                createdAt: parseDateTime(dealData[fieldKeys.createdAt]),
                updatedAt: parseDateTime(dealData[fieldKeys.updatedAt]),
                contactCompanyId: dealData["org_id"]?.value,
                dealStageId: dealData[fieldKeys.dealStage],
                status: dealData["status"] as DealStatus,
                value: dealData["value"],
                lostReason: dealData["lost_reason"] ?? null,
                propertyOwnerId: dealData[fieldKeys.propertyOwner]?.value,
                OID: dealData[fieldKeys.OID],
                tenant: dealData[fieldKeys.tenant],
                brokerId: dealData[fieldKeys.broker]?.value,
                zoning: dealData[fieldKeys.zoning],
                askingPrice: dealData[fieldKeys.askingPrice],
                buildingSize: dealData[fieldKeys.buildingSize],
                landSizeAcres: dealData[fieldKeys.landSizeAcres],
                usableLand: dealData[fieldKeys.usableLand],
                marketRentBuilding: fieldKeys.marketRentBuilding ? dealData[fieldKeys.marketRentBuilding] : null,
                marketRentLand: fieldKeys.marketRentLand ? dealData[fieldKeys.marketRentLand] : null,
                dropboxLink: dealData[fieldKeys.dropboxLink],
                parcelId: dealData[fieldKeys.parcelId],
                regridId: fieldKeys.regridId ? dealData[fieldKeys.regridId] : null,
                nonFund: nonFundOptions.find(sm => sm.id.toString() === dealData[fieldKeys.nonFund])?.label || null,
                dealProfile: profileOptions.find(sm => sm.id.toString() === dealData[fieldKeys.dealProfile])?.label || null,
                sourcingMethod: sourcingMethodOptions.find(sm => sm.id.toString() === dealData[fieldKeys.sourcingMethod])?.label || null,
                sold: fieldKeys.sold ? (soldOptions.find(sm => sm.id.toString() === dealData[fieldKeys.sold!])?.label || null) : null,
                labelId: dealData.label ? Number(dealData.label) : null,
                leaseCompIds: leaseCompIdsString && isJsonString(leaseCompIdsString) ? JSON.parse(leaseCompIdsString) : [],
                saleCompIds: saleCompIdsString && isJsonString(saleCompIdsString) ? JSON.parse(saleCompIdsString) : [],
            }

            if (!Array.isArray(deal.dealShapes) && deal.dealShapes)
                deal.dealShapes = [deal.dealShapes];

            return deal;
        });
    }

    private dealCreated(deal: IDeal): Promise<void> {
        return api.post("/deal/created", deal);
    }

    private dealUpdated(deal: IDeal): Promise<void> {
        return api.post("/deal/updated", deal);
    }

    private IsJsonString(str: string) {
        try {
            JSON.parse(str);
        } catch (e) {
            return false;
        }
        return true;
    }
}

const isJsonString = (str: string): boolean => {
    try {
        JSON.parse(str);
        return true;
    } catch {
        return false;
    }
}